import React from "react";
import {
  EmptyState,
  EmptyStateProps,
  LoadPhase,
  RowActionsMenu,
  SkeletonCard,
} from "../../../components/ui";
import type { RowAction } from "../../../components/ui";
// The same tick and cross the table answers an unmarked row with — the same
// act on the same record, so the same control. See `PaymentAnswer`.
import PaymentAnswer from "../../payments/components/PaymentAnswer";
import type { GateProps } from "../../billing/hooks/useSubscriptionGate";
import { agendaLabel } from "../meetingAgenda";
import {
  appointmentDate,
  appointmentState,
  appointmentTime,
  durationLabel,
  isUpcoming,
} from "../appointments";
import type {
  AppointmentState,
  ClientAppointment,
} from "../types/appointments.types";

/** The height the cards stand in at while the history is on its way — about
 *  three cards, which is what the tab shows before the fold. */
const CARDS_SKELETON = "h-[320px]";

export interface AppointmentCardsProps {
  appointments: ClientAppointment[];
  /** Stands in for the cards when the history comes back with none. */
  empty: EmptyStateProps;
  /** Where the history is — the same contract the table works to. */
  phase?: LoadPhase;
  /**
   * The tab's one `now`, handed over rather than read here: the carousel, the
   * table and these cards all have to agree about whether an appointment has
   * started, and a second clock in this file is how they would stop agreeing.
   */
  now: Date;
  /** The tint each settled state is drawn in, handed over so the two layouts
   *  cannot end up colouring the same status differently. */
  statusStyles: Record<Exclude<AppointmentState, "Awaiting">, string>;
  /** What the two answers on an unmarked card wear while the workspace has no
   *  plan behind it. */
  answerGate: GateProps;
  /** The same, for the two entries on a standing card's menu. */
  rescheduleGate: GateProps;
  cancelGate: GateProps;
  /** Opens the appointment — the whole record, in the dialog the table's rows
   *  open too. */
  onOpen: (appointment: ClientAppointment) => void;
  onReschedule: (appointment: ClientAppointment) => void;
  onCancel: (appointment: ClientAppointment) => void;
  onComplete: (appointment: ClientAppointment) => void;
}

/**
 * A client's appointments as they read on a phone: one card per booking
 * instead of a table, and no card around the lot of them.
 *
 * The table needs 900px for its eight columns, and on a phone that is a card
 * scrolled sideways inside a page — with Status and the row menu, the two
 * things anybody is here to act on, last off the right-hand edge. So the row
 * becomes a card carrying what the columns held: when it is, what it is for,
 * how it is held and for how long, the way in or the way there, and whatever
 * became of it.
 *
 * Every field is decided per card rather than per tab, which is where this
 * parts company with the table. There the Link/Address, Reason and Note
 * columns are switched by which tab is open, because a column that is empty
 * for four rows in five has still taken the width of one that is not. A card
 * has no columns to keep in line: the reason is on the cancelled card and
 * nowhere else, the note is on the one that was marked, and a phone reading
 * All sees both rather than neither.
 *
 * Pressing the card opens the appointment, which is what the table's rows do
 * too — and the answers, the menu and the Join link stop that press, since
 * they act on the card rather than follow it.
 *
 * Which of the two renders is `ClientAppointmentsTab`'s decision — see
 * `useIsMobile`.
 */
const AppointmentCards: React.FC<AppointmentCardsProps> = ({
  appointments,
  empty,
  phase = "ready",
  now,
  statusStyles,
  answerGate,
  rescheduleGate,
  cancelGate,
  onOpen,
  onReschedule,
  onCancel,
  onComplete,
}) => {
  if (phase !== "ready" && appointments.length === 0) {
    return (
      <div className="flex-1 min-h-0">
        <SkeletonCard phase={phase} className={CARDS_SKELETON} />
      </div>
    );
  }

  if (appointments.length === 0) {
    // A column rather than a row, so the state gets the full width to set its
    // lines in. It brings its own padding.
    return (
      <div className="flex-1 min-h-0 flex flex-col justify-center">
        <EmptyState {...empty} />
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-overlay">
      <ul className="flex flex-col gap-3 pb-4">
        {appointments.map((appointment) => {
          const state = appointmentState(appointment, now);
          // Still going to happen — which is what everything this card offers
          // depends on. Asked of the record rather than of the pill, which
          // also says "Awaiting" for a booking whose time has passed. See
          // `isUpcoming`.
          const ahead = isUpcoming(appointment, now);
          // A link worth pressing, which a finished or cancelled
          // appointment's is not: the call is over or was never going to
          // happen, and offering a way into it invites somebody to sit in an
          // empty room.
          const joinable =
            ahead && appointment.type === "Online" && !!appointment.meetingUrl;
          // Only a standing appointment still ahead has anything to do to it.
          // Moving one that has already happened would be rewriting history,
          // and a cancelled one is not revived by giving it a new time.
          const actionable = ahead;
          // The words the answers and the menu are named by, since an icon has
          // none of its own and the date is what tells two cards apart.
          const which = appointmentDate(appointment.startsAt);

          // Greyed rather than dropped while the workspace is shut, the way
          // the table's menu does it — a locked entry selects into the gate's
          // own explanation rather than into nothing.
          const gatedAction = (
            label: string,
            gate: GateProps,
            onSelect: () => void
          ): RowAction => ({
            label,
            disabled: gate.disabled,
            onSelect: gate.disabled
              ? gate.onDisabledClick ?? (() => {})
              : onSelect,
          });

          return (
            <li key={appointment.id}>
              {/* The whole card is pressable for a thumb, and the date inside
                  it is a real button so the list stays reachable by keyboard
                  — the arrangement the table gives its rows, and the reason
                  it has to be: this card also holds a menu and, on an
                  unmarked one, two more buttons. A control inside a control is
                  markup no browser agrees on, so the card carries no role of
                  its own.

                  `active:` rather than `hover:` — a phone has no pointer to
                  hover with, and the press is what wants acknowledging. */}
              <div
                onClick={() => onOpen(appointment)}
                className="w-full cursor-pointer card-surface border border-line bg-white p-4 text-left transition-colors active:bg-[#F8FEFF]"
              >
                <div className="flex items-start gap-3">
                  {/* `min-w-0` is what lets a long agenda shorten instead of
                      pushing the corner off the card's edge. */}
                  <div className="min-w-0 flex-1">
                    <h3 className="text-[15px] font-medium text-ink">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpen(appointment);
                        }}
                        className="max-w-full rounded text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
                      >
                        {which}
                        {/* The time beside the date rather than under it: the
                            two are one answer to "when", and the card's
                            second line is what the appointment was for. */}
                        <span className="font-normal text-ink-muted">
                          {" · "}
                          {appointmentTime(appointment.startsAt)}
                        </span>
                        <span className="sr-only"> — open this appointment</span>
                      </button>
                    </h3>
                    {/* A dash where none was chosen: the booking dialog asks
                        but does not insist, and a blank line would read as one
                        lost rather than as one never filled in. */}
                    <p className="mt-0.5 truncate text-[13px] text-ink-muted">
                      {agendaLabel(appointment.agenda) || "-"}
                    </p>
                  </div>

                  {/* A card whose time has passed with nobody saying what
                      became of it is not a state to report so much as a
                      question to answer, so its corner is the two answers
                      rather than a pill. The two never share the corner with
                      the menu: a card is only asked about once its time has
                      passed, and only a card still ahead has a menu. */}
                  <div
                    className="flex flex-shrink-0 items-center gap-1"
                    onClick={(event) => event.stopPropagation()}
                  >
                    {state === "Awaiting" ? (
                      <>
                        {/* Said for a screen reader, which cannot see that
                            the corner holds a tick and a cross rather than a
                            word. */}
                        <span className="sr-only">Waiting to be marked</span>
                        <PaymentAnswer
                          tone="confirm"
                          label={`Mark the ${which} appointment as completed`}
                          gate={answerGate}
                          onClick={() => onComplete(appointment)}
                        />
                        {/* The cross is the same act as calling off an
                            appointment still ahead, and goes to the same
                            dialog: the backend keeps one state for a session
                            that did not take place however it failed to. */}
                        <PaymentAnswer
                          tone="cancel"
                          label={`Cancel the ${which} appointment — the session did not take place`}
                          gate={answerGate}
                          onClick={() => onCancel(appointment)}
                        />
                      </>
                    ) : (
                      <span
                        className={`whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-medium ${statusStyles[state]}`}
                      >
                        {state}
                      </span>
                    )}

                    {actionable && (
                      <RowActionsMenu
                        label={`Actions for the ${which} appointment`}
                        actions={[
                          gatedAction("Reschedule", rescheduleGate, () =>
                            onReschedule(appointment)
                          ),
                          {
                            ...gatedAction("Cancel", cancelGate, () =>
                              onCancel(appointment)
                            ),
                            destructive: true,
                          },
                        ]}
                      />
                    )}
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-between gap-3 border-t border-line pt-3">
                  {/* How it is held and for how long, on one line and each
                      readable without a heading over it — "Online" and
                      "1 hr 30 min" say what they are. */}
                  <span className="min-w-0 truncate text-[12px] text-ink-subtle">
                    {appointment.type} ·{" "}
                    {durationLabel(appointment.durationMinutes)}
                  </span>

                  {/* The way in, where there is one worth pressing. Stopped
                      here rather than left to the card, so a press that lands
                      on the link joins the call instead of opening a dialog
                      over the tab somebody was about to leave for. */}
                  {joinable && (
                    <a
                      href={appointment.meetingUrl!}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(event) => event.stopPropagation()}
                      className="inline-flex flex-shrink-0 items-center gap-1.5 text-[13px] font-medium text-[#00A5B4] transition-colors active:text-[#007E8C]"
                    >
                      <span
                        aria-hidden="true"
                        className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#00BACB]"
                      />
                      Join link
                      <span className="sr-only"> for the {which} appointment</span>
                    </a>
                  )}
                </div>

                {/* The three prose fields the table switches columns between,
                    each on the card that has something to put in it and
                    nowhere else — a card with no address has no line about
                    one, rather than a dash standing in for a column that had
                    to be drawn either way.

                    Named, because a card has no column heading over it to say
                    which of the three a sentence is. Two lines each at most:
                    there is no hover on a phone to finish a clipped one in, so
                    an address or a reason gets the room to be read, and a
                    paragraph that would run away with the card still stops.
                    `whitespace-pre-line`, so the line breaks an address
                    arrives with are kept. */}
                {appointment.type === "Offline" && appointment.address && (
                  <p className="mt-2 line-clamp-2 whitespace-pre-line text-[12px] leading-[1.5] text-ink-subtle">
                    <span className="text-ink-muted">Address: </span>
                    {appointment.address}
                  </p>
                )}

                {appointment.cancelReason && (
                  <p className="mt-2 line-clamp-2 text-[12px] leading-[1.5] text-ink-subtle">
                    <span className="text-ink-muted">Reason: </span>
                    {appointment.cancelReason}
                  </p>
                )}

                {appointment.completionNote && (
                  <p className="mt-2 line-clamp-2 text-[12px] leading-[1.5] text-ink-subtle">
                    <span className="text-ink-muted">Note: </span>
                    {appointment.completionNote}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default AppointmentCards;
